// Catálogo de eventos de webhook (espelha internal/webhook/model.go). Agrupado
// para a seleção na UI — o cliente escolhe só o que lhe interessa.

export interface WebhookEvent {
  type: string;
  label: string;
  hint: string;
  /** Só existe no trilho do QR code (no oficial nunca dispara). */
  unofficialOnly?: boolean;
  /** Só existe no WhatsApp oficial. */
  officialOnly?: boolean;
}

export interface WebhookEventGroup {
  group: string;
  events: WebhookEvent[];
  /** Só existe no WhatsApp oficial (a Meta é quem decide sobre número, conta e template). */
  officialOnly?: boolean;
  /** Só existe no trilho do QR code (a API oficial não tem grupos). */
  unofficialOnly?: boolean;
}

export const WEBHOOK_EVENTS: WebhookEventGroup[] = [
  {
    group: 'Mensagens',
    events: [
      { type: 'message.received', label: 'Mensagem recebida', hint: 'Toda mensagem 1:1 ou de grupo que chega (texto, mídia, voto de enquete…).' },
      { type: 'message.sent', label: 'Mensagem enviada', hint: 'Sua mensagem foi aceita pelo WhatsApp.' },
      { type: 'message.delivered', label: 'Entregue', hint: 'Entregue ao aparelho do destinatário (✓✓).' },
      { type: 'message.read', label: 'Lida', hint: 'O destinatário leu a mensagem.' },
      { type: 'message.failed', label: 'Falhou', hint: 'Não foi possível enviar.' },
    ],
  },
  {
    group: 'Contatos',
    events: [
      { type: 'contact.opted_out', label: 'Pediu para sair', hint: 'Respondeu PARAR/SAIR — ou, no oficial, parou o marketing no próprio WhatsApp (reason marketing_opt_out).' },
      { officialOnly: true, type: 'contact.opted_in', label: 'Voltou a aceitar marketing', hint: 'No oficial, o contato retomou as mensagens de marketing no WhatsApp.' },
    ],
  },
  {
    group: 'Grupos',
    unofficialOnly: true,
    events: [
      { type: 'group.participant_added', label: 'Entrou no grupo', hint: 'Um ou mais participantes entraram/foram adicionados.' },
      { type: 'group.participant_removed', label: 'Saiu do grupo', hint: 'Participante saiu ou foi removido.' },
      { type: 'group.participant_promoted', label: 'Virou admin', hint: 'Participante promovido a administrador.' },
      { type: 'group.participant_demoted', label: 'Deixou de ser admin', hint: 'Administrador rebaixado a membro.' },
      { type: 'group.subject_changed', label: 'Nome alterado', hint: 'O nome do grupo mudou.' },
      { type: 'group.description_changed', label: 'Descrição alterada', hint: 'A descrição/tópico do grupo mudou.' },
      { type: 'group.joined', label: 'Número entrou no grupo', hint: 'Seu número foi adicionado a um grupo.' },
      { type: 'group.left', label: 'Número saiu do grupo', hint: 'Seu número saiu, foi removido ou o grupo foi apagado.' },
    ],
  },
  {
    group: 'Números & conexão',
    events: [
      { type: 'instance.connected', label: 'Número conectado', hint: 'Um número ficou online.' },
      { unofficialOnly: true, type: 'instance.warming', label: 'Aquecendo', hint: 'Número em fase de aquecimento.' },
      { type: 'instance.disconnected', label: 'Desconectado', hint: 'Queda temporária (reconecta sozinho).' },
      { unofficialOnly: true, type: 'instance.logged_out', label: 'Deslogado', hint: 'Sessão expirou — precisa reparear.' },
      { unofficialOnly: true, type: 'instance.banned', label: 'Banido', hint: 'O número foi bloqueado pelo WhatsApp.' },
      { unofficialOnly: true, type: 'qr_code', label: 'QR code', hint: 'Novo QR para parear (fluxo de conexão).' },
      { unofficialOnly: true, type: 'pairing_code', label: 'Código de pareamento', hint: 'Código de pareamento por telefone.' },
    ],
  },
  {
    group: 'WhatsApp oficial',
    officialOnly: true,
    events: [
      { type: 'template.approved', label: 'Template aprovado', hint: 'A Meta aprovou o template: já dá para enviar.' },
      { type: 'template.rejected', label: 'Template reprovado', hint: 'A Meta reprovou o template; o motivo vem junto.' },
      { type: 'template.paused', label: 'Template pausado', hint: 'Qualidade baixa: a Meta pausou o template.' },
      { type: 'template.quality_changed', label: 'Qualidade do template', hint: 'A qualidade do template mudou (verde, amarela ou vermelha).' },
      { type: 'template.categorized', label: 'Template recategorizado', hint: 'A Meta mudou a categoria — e com ela o preço da mensagem.' },
      { type: 'instance.quality_changed', label: 'Qualidade do número', hint: 'A qualidade do número mudou (verde, amarela ou vermelha).' },
      { type: 'instance.limit_changed', label: 'Limite de envio', hint: 'O limite de conversas do número subiu ou desceu.' },
      { type: 'instance.name_decided', label: 'Nome de exibição', hint: 'A Meta aprovou ou reprovou o nome de exibição pedido.' },
      { type: 'account.status_changed', label: 'Estado da conta', hint: 'A conta passou a ativa, suspensa ou aguardando pagamento.' },
      { type: 'account.alert', label: 'Aviso da Meta', hint: 'Aviso da Meta sobre a conta, com a gravidade.' },
    ],
  },
  {
    group: 'Conta',
    events: [
      { type: 'advisory.published', label: 'Ação necessária na integração', hint: 'Uma mudança nossa exige atualizar seu código (SDK, payload ou endpoint). Só dispara para quem é afetado — nunca é changelog.' },
    ],
  },
];

export const ALL_EVENT_TYPES = WEBHOOK_EVENTS.flatMap((g) => g.events.map((e) => e.type));

/** Grupos que valem para o projeto: os do oficial só aparecem em projeto oficial. */
export function eventGroupsFor(official: boolean): WebhookEventGroup[] {
  return WEBHOOK_EVENTS
    .filter((g) => (official ? !g.unofficialOnly : !g.officialOnly))
    .map((g) => ({ ...g, events: g.events.filter((e) => (official ? !e.unofficialOnly : !e.officialOnly)) }))
    .filter((g) => g.events.length > 0);
}

const LABELS = Object.fromEntries(WEBHOOK_EVENTS.flatMap((g) => g.events.map((e) => [e.type, e.label] as const)));

/** Rótulo amigável de um event_type (fallback: o próprio type). */
export function eventLabel(type: string): string {
  return LABELS[type] ?? type;
}
